import React from 'react';

import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useIconColorHex } from '@/app/common/icons/Icon';
import { starfishCssUrl } from '@/app/common/icons/starfish';
import { getExpirationDate, isParseable } from '@/app/common/components/Promotions/FlashSalesCounter/utils.ts';
import type { FlashSalesConfig } from '@/app/components/search/components/Card/types.ts';
import {
  FLASH_COUNTER_EVOLUTION_ICON,
  FLASH_COUNTER_ICON,
  LMD_COUNTER_EVOLUTION_ICON,
  LMD_COUNTER_ICON,
  FlashSalesCounterContainer,
  FlashSalesCounterContainerSlim,
  FlashSalesCounterEvolutionContainer,
} from './styled.ts';
import useCountdown, { type CountdownValues } from '../../../hooks/useCountDown.ts';

type Props = {
  flashSalesConfig: FlashSalesConfig;
  right?: boolean;
  spacedSeparators?: boolean;
};

type CountdownLabelParams = {
  countdown: CountdownValues;
  counterTranslations: NonNullable<NonNullable<FlashSalesConfig>['counterTranslations']>;
  expiredLabel: string;
  sep: string;
};

/** One string, so the parent sets it as text content and browser translate has no text node to orphan */
const countdownLabel = ({
  countdown: { days, hours, minutes, seconds },
  counterTranslations,
  expiredLabel,
  sep,
}: CountdownLabelParams) => {
  if (days + hours + minutes + seconds <= 0) return expiredLabel;

  return [
    days ? `${days}${counterTranslations.d}` : null,
    days || hours ? `${hours}${counterTranslations.h}` : null,
    days || hours || minutes ? `${minutes}${counterTranslations.m}` : null,
    !days ? `${seconds}${counterTranslations.s}` : null,
  ]
    .filter(Boolean)
    .join(sep);
};

const FlashSalesCounter: React.FC<Props> = ({ flashSalesConfig, right = false, spacedSeparators = false }) => {
  const { localization } = usePageWrapperData();
  const onDarkHex = useIconColorHex('iconOnDark');
  const counterTranslations = flashSalesConfig?.counterTranslations;
  const expiration = flashSalesConfig?.expiration;
  const expirationLabel = flashSalesConfig?.expirationLabel;
  const expiredLabel = flashSalesConfig?.expiredLabel;
  const expirationDate = getExpirationDate(expiration || '');
  const showCounter =
    counterTranslations && expiration && expirationLabel && expiredLabel && isParseable(expirationDate);
  const prefix = expirationDate && expirationLabel;
  const countdown = useCountdown(expirationDate.toString());
  const lastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;
  const isExpiring = countdown.days < 1;
  const sep = spacedSeparators ? ' : ' : ':';

  if (!showCounter) return null;

  return (
    <FlashSalesCounterContainer
      lastMinuteDeals={lastMinuteDeals}
      isExpiring={isExpiring}
      lmdIcon={starfishCssUrl('Brands and products', 'icon-lastminute-deals', onDarkHex, LMD_COUNTER_ICON)}
      flashIcon={starfishCssUrl('Brands and products', 'icon-flash-sales', onDarkHex, FLASH_COUNTER_ICON)}
      right={right}
    >
      <div>{prefix}</div>
      <div suppressHydrationWarning={true}>{countdownLabel({ countdown, counterTranslations, expiredLabel, sep })}</div>
    </FlashSalesCounterContainer>
  );
};

type EvolutionProps = {
  flashSalesConfig: FlashSalesConfig;
  spacedSeparators?: boolean;
};

export const FlashSalesCounterEvolution: React.FC<EvolutionProps> = ({ flashSalesConfig, spacedSeparators = true }) => {
  const { localization } = usePageWrapperData();
  const onDarkHex = useIconColorHex('iconOnDark');
  const primaryHex = useIconColorHex('iconPrimary');

  const counterTranslations = flashSalesConfig?.counterTranslations;
  const expiration = flashSalesConfig?.expiration;
  const expirationLabel = flashSalesConfig?.expirationLabel;
  const expiredLabel = flashSalesConfig?.expiredLabel;

  const expirationDate = getExpirationDate(expiration || '');
  const showCounter =
    counterTranslations && expiration && expirationLabel && expiredLabel && isParseable(expirationDate);
  const countdown = useCountdown(expirationDate.toString());
  const lastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;
  const isExpiring = countdown.days < 1;
  const sep = spacedSeparators ? ' : ' : ':';

  if (!showCounter) return null;

  return (
    <FlashSalesCounterEvolutionContainer
      lastMinuteDeals={lastMinuteDeals}
      isExpiring={isExpiring}
      lmdIcon={starfishCssUrl('Brands and products', 'icon-lastminute-deals', onDarkHex, LMD_COUNTER_EVOLUTION_ICON)}
      flashIcon={starfishCssUrl('Brands and products', 'icon-flash-sales', primaryHex, FLASH_COUNTER_EVOLUTION_ICON)}
    >
      <div suppressHydrationWarning={true}>{countdownLabel({ countdown, counterTranslations, expiredLabel, sep })}</div>
    </FlashSalesCounterEvolutionContainer>
  );
};

export const FlashSalesCounterSlim: React.FC<Props> = ({
  flashSalesConfig,
  spacedSeparators = false,
  ...rest
}: Props & React.HTMLAttributes<HTMLDivElement>) => {
  const counterTranslations = flashSalesConfig?.counterTranslations;
  const expiration = flashSalesConfig?.expiration;
  const expirationLabel = flashSalesConfig?.expirationLabel;
  const expiredLabel = flashSalesConfig?.expiredLabel;
  const expirationDate = getExpirationDate(expiration || '');
  const prefix = expirationDate && expirationLabel;
  const countdown = useCountdown(expirationDate.toString());
  const showCounter =
    counterTranslations && expiration && expirationLabel && expiredLabel && isParseable(expirationDate);
  const sep = spacedSeparators ? ' : ' : ':';

  if (!showCounter) return null;

  return (
    <FlashSalesCounterContainerSlim isExpiring={countdown.days < 1} {...rest}>
      <div>{prefix}</div>
      <div suppressHydrationWarning={true}>{countdownLabel({ countdown, counterTranslations, expiredLabel, sep })}</div>
    </FlashSalesCounterContainerSlim>
  );
};

export default FlashSalesCounter;
